import { type FC, type ReactNode, useRef, useEffect } from 'react';
import debounce from 'lodash/debounce.js';
import { isMobile } from 'dibs-media-query';
import { userServerVars } from '../../userServerVars.ts';
import { isNavbarExpandable } from '../../helpers/isNavbarExpandable.ts';
import { useNavContext } from '../../NavContext.tsx';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './styles/NavLayoutContainer.scss';

export const NavLayoutContainer: FC<{ children: ReactNode }> = ({ children }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { status, setStatus, setExpandedArea } = useNavContext();
    const isMobileView = isMobile();

    useEffect(() => {
        function checkOutsideClick(e: Event): void {
            if (
                status === 'overlay' &&
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setStatus('collapsed');
                setExpandedArea(null);
            }
        }

        const debouncedOutsideClick = debounce(checkOutsideClick, 100);
        // must add listener and remove on each render because the function will end up being different
        // each time this is rendered.
        // https://codedaily.io/tutorials/72/Creating-a-Reusable-Window-Event-Listener-Hook-with-useEffect-and-useCallback
        if (!isMobileView && !isNavbarExpandable() && status === 'overlay') {
            document.addEventListener('click', debouncedOutsideClick, true);
        }

        return () => {
            document.removeEventListener('click', debouncedOutsideClick, true);
        };
    }, [status, containerRef, isMobileView, setStatus, setExpandedArea]);

    const { isDealer } = userServerVars;

    const containerClassNames = isMobileView
        ? classnames(dibsCss.zOverlay, {
              [styles.mobileNavCollapsed]: status === 'collapsed',
              [dibsCss.hScreen]: status === 'overlay',
              [dibsCss.borderB]: isDealer && status === 'collapsed',
              [dibsCss.borderBSolid]: isDealer && status === 'collapsed',
              [dibsCss.borderBDolphin]: isDealer && status === 'collapsed',
          })
        : classnames(styles.sideBar, dibsCss.hScreen, dibsCss.zNav, {
              [styles.sideBarWidth]: status === 'expanded' || status === 'overlay',
              [styles.sideBarWidthCollapsed]: status === 'collapsed',
              [styles.sideShadow]: status === 'overlay',
              [dibsCss.borderR]: isDealer,
              [dibsCss.borderRSolid]: isDealer,
              [dibsCss.borderRDolphin]: isDealer,
          });

    return (
        <div
            ref={containerRef}
            className={classnames(
                containerClassNames,
                dibsCss.fixed,
                dibsCss.top0,
                dibsCss.flex,
                dibsCss.flexCol,
                {
                    [dibsCss.bgWhite]: isDealer,
                    [dibsCss.bgNoir]: !isDealer,
                    [dibsCss.overflowYScroll]: status === 'expanded' || status === 'overlay',
                }
            )}
            onMouseLeave={() => {
                if (isNavbarExpandable() && status === 'overlay') {
                    setStatus('collapsed');
                    setExpandedArea(null);
                }
            }}
        >
            {children}
        </div>
    );
};
